import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuthStore } from '@/stores/authStore';
import { Panel } from '@/components/ui/Panel';
import { Button } from '@/components/ui/Button';
import { BrandLogos } from '@/components/layout/BrandLogos';

/**
 * Where an invitation or a password-reset link lands.
 *
 * The Supabase client is deliberately created with `detectSessionInUrl:
 * false`, so nothing consumes the token behind our back — this page is the
 * only place in the app that reads one, and it does it once.
 *
 * The token arrives in the URL fragment. A fragment is never sent to a
 * server, but it does survive a screenshot and a shared browser history, so
 * it is wiped from the address bar as soon as it has been exchanged.
 */

const MIN_LENGTH = 8;

type Phase = 'checking' | 'ready' | 'expired' | 'done';

export function SetPassword() {
  const navigate = useNavigate();
  const profile = useAuthStore((s) => s.profile);
  const refreshProfile = useAuthStore((s) => s.refreshProfile);
  // Sent here by RequireAuth rather than by a link: the account was created
  // with a password somebody else chose.
  const forced = Boolean(profile?.must_change_password);
  const [phase, setPhase] = useState<Phase>('checking');
  const [email, setEmail] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
      const query = new URLSearchParams(window.location.search);

      const problem = hash.get('error_description') ?? query.get('error_description');
      const accessToken = hash.get('access_token');
      const refreshToken = hash.get('refresh_token');

      // Clear the address bar before anything can go wrong with it.
      if (window.location.hash || window.location.search) {
        window.history.replaceState(null, '', window.location.pathname);
      }

      if (problem) {
        if (!cancelled) { setError(problem); setPhase('expired'); }
        return;
      }

      if (accessToken && refreshToken) {
        const { data, error: err } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
        });
        if (cancelled) return;
        if (err) { setError(err.message); setPhase('expired'); return; }
        setEmail(data.user?.email ?? null);
        setPhase('ready');
        return;
      }

      // Someone already signed in can set a new password from here too —
      // reaching this page without a link is not an error, and a forced
      // change arrives exactly this way.
      const { data } = await supabase.auth.getSession();
      if (cancelled) return;
      if (data.session) {
        setEmail(data.session.user.email ?? null);
        setPhase('ready');
      } else {
        setPhase('expired');
      }
    })();

    return () => { cancelled = true; };
  }, []);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    if (password.length < MIN_LENGTH) {
      setError(`Use at least ${MIN_LENGTH} characters.`);
      return;
    }
    if (password !== confirm) {
      setError('The two passwords do not match.');
      return;
    }

    setBusy(true);
    const { error: err } = await supabase.auth.updateUser({ password });

    if (err) { setBusy(false); setError(err.message); return; }

    // Supabase owns the password; this only records that the administrator's
    // one is no longer in use, which is what releases the gate in
    // RequireAuth. A failure here would trap the user on this screen, so it
    // is reported rather than swallowed.
    const { error: flagErr } = await supabase.rpc('clear_password_change_flag');
    if (flagErr) {
      setBusy(false);
      setError(`Your password was changed, but the account could not be released: ${flagErr.message}`);
      return;
    }
    await refreshProfile();
    setBusy(false);
    setPhase('done');
    // Straight in — they are already signed in by the link they followed.
    setTimeout(() => navigate('/', { replace: true }), 1200);
  }

  return (
    <div className="login">
      <div className="login__card">
        <BrandLogos compact />

        <Panel>
          {phase === 'checking' && <p className="muted small">Checking your link…</p>}

          {phase === 'expired' && (
            <>
              <div className="login__error">
                {error ?? 'This link has expired or has already been used.'}
              </div>
              <p className="small muted">
                Invitation and reset links are valid for 24 hours and work once. Ask an
                administrator to send a new one.
              </p>
              <Button variant="secondary" onClick={() => navigate('/login', { replace: true })}>
                Back to sign in
              </Button>
            </>
          )}

          {phase === 'done' && (
            <p className="small">Password set. Taking you in…</p>
          )}

          {phase === 'ready' && (
            <form onSubmit={onSubmit}>
              {error && <div className="login__error">{error}</div>}

              {forced && (
                <p className="small" style={{ marginTop: 0 }}>
                  Your account was set up with a password somebody else chose.
                  Pick your own before carrying on — everything you capture is
                  recorded under your name, so it should be a password only you
                  know.
                </p>
              )}

              <p className="small muted" style={{ marginTop: 0 }}>
                {email ? <>Choose a password for <strong>{email}</strong>.</> : 'Choose a password.'}
              </p>

              <div className="login__field">
                <label className="login__label" htmlFor="new-password">New password</label>
                <input
                  id="new-password"
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoFocus
                />
              </div>

              <div className="login__field">
                <label className="login__label" htmlFor="confirm-password">Repeat it</label>
                <input
                  id="confirm-password"
                  type="password"
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                />
              </div>

              <p className="small muted">
                At least {MIN_LENGTH} characters. The quarry office is a shared space —
                do not reuse a password from anywhere else.
              </p>

              <Button type="submit" loading={busy} style={{ width: '100%' }}>
                {forced ? 'Set my password and continue' : 'Set password and sign in'}
              </Button>
            </form>
          )}
        </Panel>
      </div>
    </div>
  );
}
